import {
  Alert,
  Button,
  Container,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import type { ReactElement } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { readRedirectTarget } from "../auth/redirectTarget";
import { HTTP_FORBIDDEN, HttpError } from "../features/auth/api";
import { decideOrgSelection } from "../features/auth/orgSelection";
import { useAvailableOrganizations } from "../features/auth/useAvailableOrganizations";
import { useLogoutAction } from "../features/auth/useLogout";
import { useSwitchOrganization } from "../features/auth/useSwitchOrganization";

// Страница `/select-org` (ADR-0038, фаза 2) — единственное место, где
// живёт кардинальность членства (`decideOrgSelection`) и вызов
// `switchOrganization`. Живёт вне `<AppLayout>` (навбар ведёт на
// org-scoped страницы, бесполезные без активной org), но внутри
// `<ProtectedRoute>` (нужна аутентифицированная сессия).
//
// `auto` (ровно одна организация) и `picker` (несколько) рендерят
// ОДНУ И ТУ ЖЕ разметку списка организаций — осознанно: если
// авто-`switch` упадёт с 403 (членство отозвали между `list` и
// `switch`), эффект больше не выстрелит повторно (`canSwitch` станет
// `false`), и пользователь обязан увидеть рабочий экран со списком и
// объяснением ошибки, а не вечный лоадер.
export function SelectOrgPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const target = readRedirectTarget(location.state);

  const orgs = useAvailableOrganizations();
  const switchOrg = useSwitchOrganization();
  const logout = useLogoutAction();

  // Единственное вычисление решения на весь компонент — и для deps
  // эффекта (нужен ДО того, как `orgs.data` успевает TS-узко
  // типизироваться через `isPending`/`isError`, которые проверяются
  // ниже, после хуков), и для веток рендера. `orgs.data ?? []` вместо
  // ручной проверки на `undefined`: во время загрузки пустой список
  // даёт `{ kind: "empty" }`, из которого `autoOrgId` для эффекта всё
  // равно получается `null` (auto-switch не запускается) — тот же
  // внешний эффект, что и явный `null`, но без нужды в отдельном
  // nullable-типе для решения и без пересчёта той же функции ниже,
  // после того как `orgs.data` определён по-настоящему.
  const decision = decideOrgSelection(orgs.data ?? []);
  const autoOrgId = decision.kind === "auto" ? decision.orgId : null;
  const canSwitch = switchOrg.isIdle;
  const switchMutate = switchOrg.mutate;

  useEffect(() => {
    if (autoOrgId === null || !canSwitch) {
      return;
    }
    switchMutate(autoOrgId, {
      onSuccess: () => {
        void navigate(target, { replace: true });
      },
    });
  }, [autoOrgId, canSwitch, switchMutate, navigate, target]);

  if (orgs.isPending) {
    return (
      <Container py="xl">
        <Stack gap="md" align="center">
          <Loader />
          <Text>{t("orgSelect.loading")}</Text>
        </Stack>
      </Container>
    );
  }

  if (orgs.isError) {
    return (
      <Container py="xl">
        <Alert color="red" title={t("orgSelect.errorHeading")}>
          <Stack gap="sm">
            <Text>{t("orgSelect.loadError")}</Text>
            <Button
              variant="light"
              onClick={() => {
                void orgs.refetch();
              }}
            >
              {t("orgSelect.retry")}
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }

  if (decision.kind === "empty") {
    return (
      <Container py="xl">
        <Stack gap="md">
          <Title order={1}>{t("orgSelect.emptyTitle")}</Title>
          <Text>{t("orgSelect.emptyDescription")}</Text>
          <Button onClick={logout}>{t("nav.logout")}</Button>
        </Stack>
      </Container>
    );
  }

  // `auto` и `picker` попадают сюда — одна и та же разметка списка
  // (см. докстринг шапки файла).
  const switchErrorBanner = ((): ReactElement | null => {
    if (!switchOrg.isError) {
      return null;
    }
    const message =
      switchOrg.error instanceof HttpError &&
      switchOrg.error.status === HTTP_FORBIDDEN
        ? t("orgSelect.notMember")
        : t("auth.genericError", { message: switchOrg.error.message });
    return (
      <Alert color="red" title={t("orgSelect.errorHeading")}>
        <Stack gap="sm">
          <Text>{message}</Text>
          <Button
            variant="light"
            onClick={() => {
              void orgs.refetch();
            }}
          >
            {t("orgSelect.refreshList")}
          </Button>
        </Stack>
      </Alert>
    );
  })();

  return (
    <Container py="xl">
      <Stack gap="md">
        <Title order={1}>{t("orgSelect.title")}</Title>
        <Text>{t("orgSelect.description")}</Text>
        {switchErrorBanner}
        <Stack gap="sm">
          {orgs.data.map((org) => (
            <Button
              key={org.org_id}
              variant="default"
              fullWidth
              rightSection={
                <Text size="sm" c="dimmed">
                  {org.slug}
                </Text>
              }
              disabled={switchOrg.isPending}
              onClick={() => {
                switchOrg.mutate(org.org_id, {
                  onSuccess: () => {
                    void navigate(target, { replace: true });
                  },
                });
              }}
            >
              {org.name}
            </Button>
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}
